import { useMutation, useQueryClient } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import { CellInput } from '../ui/Sheet';
import { CommentsButton } from './LineComments';
import { busyKind, isLongBusy } from '../availability/BusyList';
import { formatSources, formatWindow, MAX_QUANTITY, packlistKey, reservationText, reservedSpan, windowChanges, type LineView, type PacklistView, type RuleOutcome } from './types';
import { useRowGuard } from '../ui/unsaved';
import { catalogTitle, useCatalogGesture } from '../nav/catalogLink';
import { RowMenu } from '../ui/RowMenu';
import { ExtraDaysDialog, ExtraDaysNote, type ExtraDays } from '../availability/extraDays';

/** Which columns the sheet has: "Zapisz jako blok" check boxes, and the fields the role may see (rule 7a). */
export type SheetColumns = { select: boolean; quantity: boolean; window: boolean; notes: boolean };

type Props = {
  eventId: string;
  line: LineView;
  /** The category's name, on its first row (as in the PDF); null on the others. */
  sectionCell: ReactNode;
  /** Names of the kits, blocks, templates and rules the line's quantity came from, by id. */
  sourceNames: Map<string, string>;
  cols: SheetColumns;
  /** False: read only (a role without edit, or a phone). */
  editable: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  onRemove: (l: LineView) => void;
  onReload: () => void;
  /** How many lines the same item and window stand on (more than 1: "×2 na liście"). */
  duplicates?: number;
  /** Put the focus into the quantity cell (the line was just added from a quick picker). */
  focusQuantity?: boolean;
  onQuantityFocused?: () => void;
  /** Enter in the quantity cell: saved (or nothing to save); the focus goes back to the picker. */
  onQuantityDone?: (() => void) | undefined;
  ruleOutcomes?: readonly RuleOutcome[];
  /** P11: the line's comments (count, thread open or not); absent without the right to see them. */
  comments?: { count: number; open: boolean; onToggle: () => void };
  /** F28: "Dopożycz" at a shortage (absent without the right to add rentals). */
  onSubrent?: ((l: LineView) => void) | undefined;
  /** Owner 2026-10-23 23:35: on hover, „Dopożyczono: 2 × Alfa, 2 × Beta” (only for whoever sees the rentals). */
  rented?: string | undefined;
  /** Owner 2026-10-23 20:53: Ctrl+klik (Mac; Windows: Alt+klik) opens the item in the catalog with its editor. */
  openCatalog?: (() => void) | undefined;
};

type Draft = { quantity: string; notes: string };
const draftOf = (l: LineView): Draft => ({ quantity: l.quantity === undefined ? '' : String(l.quantity), notes: l.notes ?? '' });

/**
 * One line of the packlist editor, edited in place like the quote's sheet: quantity and notes are cells (Enter or
 * leaving the row saves only what changed, Esc puts the row back), the window is changed in its own cell.
 */
export function PacklistLineRow({ eventId, line, sectionCell, sourceNames, cols, editable, selected, onToggleSelect, onRemove, onReload, duplicates = 1, focusQuantity, onQuantityFocused, onQuantityDone, ruleOutcomes = [], comments, onSubrent, rented, openCatalog }: Props) {
  const queryClient = useQueryClient();
  const row = useRef<HTMLTableRowElement | null>(null);
  const quantityRef = useRef<HTMLInputElement>(null);
  const doneAfterSave = useRef(false);
  const [draft, setDraft] = useState<Draft>(() => draftOf(line));
  const [invalid, setInvalid] = useState('');
  const [editingWindow, setEditingWindow] = useState(false);
  const [firstDay, setFirstDay] = useState(line.firstDay ?? '');
  const [lastDay, setLastDay] = useState(line.lastDay ?? '');
  const [editingDays, setEditingDays] = useState(false);

  useEffect(() => {
    if (!focusQuantity || !quantityRef.current) return;
    quantityRef.current.focus();
    quantityRef.current.select();
    onQuantityFocused?.();
  }, [focusQuantity, onQuantityFocused]);

  // Only the changed fields are sent (rule 7a).
  const patch = useMutation({
    // more of gear another event reserved first: „Dodaj mimo to” / „Anuluj” (owner 2026-10-24); null: cancelled
    mutationFn: (changes: Record<string, unknown>) =>
      withConflictConfirm((confirmConflicts) => api<PacklistView>(`/api/packlists/lines/${line.id}`, { method: 'PATCH', body: { version: line.version, ...changes, ...(confirmConflicts ? { confirmConflicts } : {}) } })),
    onSuccess: (view) => {
      if (!view) {
        doneAfterSave.current = false;
        setDraft(draftOf(line));
        return;
      }
      if (doneAfterSave.current) onQuantityDone?.();
      doneAfterSave.current = false;
      queryClient.setQueryData(packlistKey(eventId), view);
      setEditingWindow(false);
    },
    onError: () => {
      doneAfterSave.current = false;
    },
  });
  const restore = useMutation({
    mutationFn: (ruleId: string) =>
      withConflictConfirm((confirmConflicts) => api<PacklistView>(`/api/packlists/lines/${line.id}/restore-rule`, { method: 'POST', body: { ruleId, version: line.version, ...(confirmConflicts ? { confirmConflicts } : {}) } })),
    onSuccess: (view) => {
      if (view) queryClient.setQueryData(packlistKey(eventId), view);
    },
  });
  const frozen = line.contributions.filter((c) => c.source === 'rule' && c.overridden && c.sourceId);
  const failed = patch.error ?? restore.error;
  const error = failed instanceof ApiError ? failed : null;
  const conflict = error?.status === 409;
  const { missing, busyIn } = line.availability;
  const original = draftOf(line);

  /** What changed, or null when nothing did; a wrong quantity sets the message and gives 'invalid'. */
  function changes(): Record<string, unknown> | null | 'invalid' {
    const out: Record<string, unknown> = {};
    if (line.quantity !== undefined && draft.quantity.trim() !== original.quantity) {
      if (!/^\d+$/.test(draft.quantity.trim()) || Number(draft.quantity) > MAX_QUANTITY) {
        setInvalid(`Ilość musi być liczbą całkowitą od 0 do ${MAX_QUANTITY.toLocaleString('pl-PL')}.`);
        return 'invalid';
      }
      out.quantity = Number(draft.quantity.trim());
    }
    if (line.notes !== undefined && draft.notes.trim() !== original.notes) out.notes = draft.notes.trim();
    setInvalid('');
    return Object.keys(out).length > 0 ? out : null;
  }
  function save(thenDone = false) {
    if (patch.isPending) return;
    const c = changes();
    if (c === 'invalid') return;
    if (c === null) {
      if (thenDone) onQuantityDone?.();
      return;
    }
    doneAfterSave.current = thenDone;
    patch.mutate(c);
  }
  function onKeyDown(e: KeyboardEvent<HTMLTableRowElement>) {
    if (!(e.target instanceof HTMLInputElement) || e.target.type === 'checkbox' || e.target.type === 'date') return;
    if (e.key === 'Enter') {
      e.preventDefault();
      save(e.target === quantityRef.current);
    } else if (e.key === 'Escape' && (draft.quantity !== original.quantity || draft.notes !== original.notes)) {
      e.preventDefault();
      e.stopPropagation();
      setDraft(original);
      setInvalid('');
    }
  }
  // owner 2026-10-23 21:14: typed and not saved (a wrong quantity, a window not applied), leaving the screen, the card
  // or the tab asks first; the save on leaving the row is waited for (`useRowGuard`)
  const windowChanged = editingWindow && (firstDay !== (line.firstDay ?? '') || lastDay !== (line.lastDay ?? ''));
  const guard = useRowGuard(editable && (draft.quantity.trim() !== original.quantity || draft.notes.trim() !== original.notes || windowChanged), async () => {
    if (windowChanged) {
      if (!firstDay || !lastDay || firstDay > lastDay) {
        setInvalid('Podaj pierwszy i ostatni dzień okna (pierwszy nie może być po ostatnim).');
        return false;
      }
      const w = windowChanges(line, firstDay, lastDay);
      if (w) await patch.mutateAsync(w);
      return true;
    }
    const c = changes();
    if (c === 'invalid') return false;
    if (c !== null) await patch.mutateAsync(c);
    return true;
  });
  // leaving the row saves it (as in a spreadsheet); moving between its own cells does not
  function onBlur(e: FocusEvent<HTMLTableRowElement>) {
    if (e.relatedTarget instanceof Node && row.current?.contains(e.relatedTarget)) return;
    if (!editingWindow && !patch.isPending) void guard.save();
  }
  function saveWindow() {
    if (!firstDay || !lastDay || firstDay > lastDay) {
      setInvalid('Podaj pierwszy i ostatni dzień okna (pierwszy nie może być po ostatnim).');
      return;
    }
    setInvalid('');
    const c = windowChanges(line, firstDay, lastDay);
    if (!c) {
      setEditingWindow(false);
      return;
    }
    patch.mutate(c);
  }

  /** „Dni rezerwacji…” (owner 2026-10-24 15:29): false when nothing was saved („Anuluj” in the conflict question). */
  async function saveDays(extraDays: ExtraDays | null): Promise<boolean> {
    // one setting per event and item (the quote's „Dni rezerwacji…” edits the same); `version`: as seen (409 otherwise)
    const view = await withConflictConfirm((confirmConflicts) =>
      api<PacklistView>(`/api/packlists/${eventId}/extra-days`, { method: 'PUT', body: { itemId: line.itemId, extraDays, version: line.extraDaysVersion ?? 0, ...(confirmConflicts ? { confirmConflicts } : {}) } }),
    );
    if (!view) return false;
    queryClient.setQueryData(packlistKey(eventId), view);
    return true;
  }

  const catalog = useCatalogGesture(openCatalog);
  const name = line.itemName;
  const canWindow = editable && line.firstDay !== undefined && line.lastDay !== undefined;
  const message = invalid || (error && !conflict ? error.message : '');

  return (
    <>
      <tr
        ref={(el) => {
          row.current = el;
          guard.ref(el);
        }}
        className={`sheet-row pl-row${missing > 0 ? ' short' : ''}`}
        data-line-id={line.id}
        title={rented}
        onKeyDown={editable ? onKeyDown : undefined}
        onBlur={editable ? onBlur : undefined}
        onClick={openCatalog ? (e) => void catalog.onClick(e) : undefined}
        onContextMenu={openCatalog ? (e) => void catalog.onContextMenu(e) : undefined}
      >
        <th scope="row" className="sheet-section-cell">
          {sectionCell}
        </th>
        {cols.select && (
          <td className="pl-check">
            {onToggleSelect && <input type="checkbox" aria-label={`Zaznacz: ${name}`} checked={!!selected} onChange={onToggleSelect} data-feature-id="packlists.save_as" />}
          </td>
        )}
        <td className="sheet-main pl-name" title={openCatalog ? catalogTitle() : undefined}>
          <span className="pl-item">{name}</span>
          {comments && <CommentsButton name={name} count={comments.count} open={comments.open} onToggle={comments.onToggle} />}
          {duplicates > 1 && (
            <span className="chip dup" data-feature-id="packlists.merge" title="Ten sam sprzęt w tym samym terminie stoi na kilku pozycjach">
              ×{duplicates} na liście
            </span>
          )}
          {/* owner 2026-10-24 15:35: „Info o zajętości dłuższej na czerwono wyraźnie” — for everyone who sees the line */}
          <ExtraDaysNote held={line.extraHeld} />
          {/* typed by hand only: the quantity says it all; kits, blocks, templates and rules are named */}
          {line.contributions.some((c) => c.source !== 'manual') && <span className="pl-sub pl-sources">Źródła: {formatSources(line, sourceNames)}</span>}
          {frozen.map((c) => {
            const proposal = ruleOutcomes.find((o) => o.ruleId === c.sourceId);
            return (
              <span key={c.sourceId} className="rule-note" data-feature-id="packlists.rules">
                <span className="chip accent">
                  poprawione ręcznie; {proposal ? (proposal.quantity !== undefined ? `reguła proponuje ${proposal.quantity}` : 'reguła proponuje inną ilość') : 'reguła już nic nie dodaje'}
                </span>
                {editable && (
                  <button className="btn small ghost" type="button" aria-label={`Przywróć regułę: ${name}`} disabled={restore.isPending} onClick={() => restore.mutate(c.sourceId!)}>
                    Przywróć regułę
                  </button>
                )}
              </span>
            );
          })}
          {missing > 0 && (
            <span className="pl-short" data-feature-id="packlists.shortage">
              {busyIn.length > 0 && (
                <>
                  Zajęte w:{' '}
                  {busyIn.map((b, i) => (
                    <span key={i}>
                      {i > 0 && ', '}
                      <span className={isLongBusy(b, reservedSpan(line).firstDay, reservedSpan(line).lastDay) ? 'busy-long' : undefined}>{`${b.groupLabel} (${b.quantity}, ${busyKind(b)})`}</span>
                    </span>
                  ))}
                  ,{' '}
                </>
              )}
              brakuje {missing}
              {onSubrent && (
                <button className="btn small ghost pl-subrent" type="button" aria-label={`Dopożycz: ${name}`} data-feature-id="subrentals.tab" onClick={() => onSubrent(line)}>
                  Dopożycz
                </button>
              )}
            </span>
          )}
          {(line.availability.inbound ?? 0) > 0 && <span className="pl-inbound">w tym dopożyczone {line.availability.inbound}</span>}
          {line.reservation.hardFailed && <span className="pl-sub">brak sprzętu — zarezerwowano wstępnie</span>}
        </td>
        {cols.quantity && (
          <td className="num pl-qty" data-label="Ilość">
            {line.quantity === undefined ? null : editable ? (
              <CellInput
                ref={quantityRef}
                numeric
                inputMode="numeric"
                aria-label={`Ilość: ${name}`}
                aria-invalid={invalid.startsWith('Ilość') ? true : undefined}
                value={draft.quantity}
                onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
              />
            ) : (
              line.quantity
            )}
          </td>
        )}
        {cols.window && (
          <td className="pl-window" data-label="Okno" data-feature-id="packlists.windows">
            {editingWindow ? (
              <span className="pl-window-edit">
                <input type="date" aria-label={`Pierwszy dzień: ${name}`} value={firstDay} onChange={(e) => setFirstDay(e.target.value)} />
                <input type="date" aria-label={`Ostatni dzień: ${name}`} value={lastDay} onChange={(e) => setLastDay(e.target.value)} />
                <span className="pl-window-actions">
                  <button className="btn small" type="button" disabled={patch.isPending} onClick={saveWindow}>
                    Zapisz okno
                  </button>
                  {line.followsEvent === false && (
                    <button className="btn small ghost" type="button" aria-label={`Całe wydarzenie: ${name}`} disabled={patch.isPending} onClick={() => patch.mutate({ followsEvent: true })}>
                      Całe wydarzenie
                    </button>
                  )}
                  <button className="btn small ghost" type="button" onClick={() => { setEditingWindow(false); setInvalid(''); }}>
                    Anuluj
                  </button>
                </span>
              </span>
            ) : canWindow ? (
              <button className="pl-window-btn" type="button" aria-label={`Zmień okno: ${name}`} title="Zmień okno" onClick={() => setEditingWindow(true)}>
                {formatWindow(line)}
              </button>
            ) : (
              formatWindow(line)
            )}
          </td>
        )}
        <td className="pl-res" data-label="Rezerwacja">
          {reservationText(line)}
        </td>
        {cols.notes && (
          <td className="sheet-note pl-notes">
            {line.notes === undefined ? null : editable ? (
              <CellInput aria-label={`Uwagi: ${name}`} maxLength={5000} placeholder="—" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            ) : (
              line.notes
            )}
          </td>
        )}
        {editable && (
          <td className="q-actions">
            {/* the days apply to the item's lines on the whole event (a line with its own days reserves exactly those) */}
            {canWindow && line.followsEvent && <RowMenu floating="end" label={`Więcej: ${name}`} featureId="packlists.extra_days" actions={[{ label: 'Dni rezerwacji…', onSelect: () => setEditingDays(true) }]} />}
            <button className="btn icon ghost q-remove" type="button" aria-label={`Usuń ze sprzętówki: ${name}`} title="Usuń ze sprzętówki" onClick={() => onRemove(line)}>
              ×
            </button>
            {editingDays && <ExtraDaysDialog name={name} value={line.extraDays} onSave={saveDays} onClose={() => setEditingDays(false)} />}
          </td>
        )}
      </tr>
      {(message || conflict) && (
        <tr className="sheet-row-error">
          <td colSpan={20} role={conflict ? undefined : 'alert'}>
            {conflict ? (
              <ConflictNotice
                onReload={() => {
                  patch.reset();
                  restore.reset();
                  onReload();
                }}
              />
            ) : (
              message
            )}
          </td>
        </tr>
      )}
    </>
  );
}
